import type { AdminPermission } from '@onov/contracts';
import {
  BarChart3,
  CreditCard,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  Megaphone,
  Radio,
  ShieldAlert,
  Star,
  Users,
  type LucideIcon,
} from 'lucide-react';

export interface NavSection {
  label: string;
  path: string;
  /** Shown when the session holds this permission — never a role name. */
  permission: AdminPermission;
  icon: LucideIcon;
  children?: Array<{ label: string; path: string; permission?: AdminPermission }>;
}

/**
 * IA per docs/01-PRD.md §9.1, filtered by PERMISSION (§4.1 via the RBAC
 * matrix — role names are never consulted, so custom roles get exactly the
 * sections their permission set implies). Hiding a link is a convenience —
 * every endpoint behind these routes re-checks server-side (AC-ADMGL-01,
 * proven by the generated matrix suite in apps/api).
 *
 * A child without its own `permission` inherits the section's; a child with
 * one is filtered separately (Admin Users and Roles are reserved-permission
 * pages inside a section Finance can see for the audit log).
 */
export const NAV: NavSection[] = [
  {
    label: 'Dashboard',
    path: '/',
    permission: 'analytics:dashboard:read',
    icon: LayoutDashboard,
  },
  {
    label: 'Moderation',
    path: '/moderation',
    permission: 'moderation:report:manage',
    icon: ShieldAlert,
    children: [
      { label: 'Report Queue', path: '/moderation' },
      { label: 'Appeals', path: '/moderation/appeals', permission: 'moderation:appeal:manage' },
    ],
  },
  { label: 'Members', path: '/members', permission: 'member:read', icon: Users },
  {
    label: 'Standing',
    path: '/standing',
    permission: 'standing:upgrade:manage',
    icon: Star,
    children: [
      { label: 'Upgrade Requests', path: '/standing' },
      { label: 'Tiers & Thresholds', path: '/standing/tiers', permission: 'standing:tier:manage' },
    ],
  },
  { label: 'Live', path: '/live', permission: 'moderation:report:manage', icon: Radio },
  {
    label: 'Commerce',
    path: '/commerce',
    permission: 'payment:read',
    icon: CreditCard,
    children: [{ label: 'Payments & Refunds', path: '/commerce' }],
  },
  { label: 'Support', path: '/support', permission: 'support:ticket:manage', icon: LifeBuoy },
  {
    label: 'Content',
    path: '/content',
    permission: 'content:policy:manage',
    icon: FileText,
    children: [
      { label: 'Policies & FAQ', path: '/content' },
      {
        label: 'Templates',
        path: '/content/templates',
        permission: 'notification:template:manage',
      },
    ],
  },
  {
    label: 'Broadcast',
    path: '/broadcast',
    permission: 'notification:broadcast:send',
    icon: Megaphone,
  },
  {
    label: 'Platform',
    path: '/platform',
    permission: 'platform:audit:read',
    icon: BarChart3,
    children: [
      { label: 'Audit Log', path: '/platform' },
      { label: 'Admin Users', path: '/platform/admins', permission: 'platform:admin:manage' },
      { label: 'Roles', path: '/platform/roles', permission: 'platform:role:manage' },
    ],
  },
];

export const navFor = (permissions: readonly string[]): NavSection[] =>
  NAV.filter((s) => permissions.includes(s.permission)).map((s) =>
    s.children
      ? {
          ...s,
          children: s.children.filter((c) => permissions.includes(c.permission ?? s.permission)),
        }
      : s,
  );
